import { Button, Drawer, Flex, Form, Grid, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { sendResetPassword } from '@/api/user';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { useApiMutation } from '@/hooks/api';
import { SPACE_MD } from '@/theme/buildAntdTheme';
import FormErrorAlert from '@/UI/FormErrorAlert';
import ModalFooter from '@/UI/ModalFooter';
import { buildResetPasswordRequestFormSchema } from '@/utils/validation/formSchemas/authSchemas';

import type { SchemaSection } from '@/components/form/SchemaForm/types';

interface DrawerProps {
  open: boolean;
  setOpen: (st: boolean) => void;
}

const ResetPasswordPopUp = ({ open, setOpen }: DrawerProps) => {
  const [email, setEmail] = useState('');
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const formSchema = useMemo(() => buildResetPasswordRequestFormSchema(t), [t]);

  const resetMutation = useApiMutation({
    mutationFn: (values: { email: string }) => sendResetPassword(values.email),
    errorKey: 'CLIENT_LOGIN_NOT_ALLOWED',
    onSuccess: (_data, values) => {
      setEmail(values.email);
    },
  });

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'reset',
        fields: [
          {
            name: 'email',
            type: 'text',
            label: t('EMAIL'),
            componentProps: { size: 'large' },
          },
        ],
      },
    ],
    [t],
  );

  const onFinish = (values: { email: string }) => {
    resetMutation.mutate(values);
  };

  return (
    <Drawer
      title=""
      placement="right"
      closable={true}
      onClose={() => {
        setOpen(!open);
        setEmail('');
        resetMutation.reset();
        form.resetFields();
      }}
      open={open}
      mask={false}
      size={isMobile ? '95%' : '33.333%'}
      footer={
        !email ? (
          <ModalFooter
            actions={
              <Button
                type="primary"
                size="large"
                loading={resetMutation.loading}
                onClick={() => form.submit()}
              >
                {t('RESET_PASSWORD_TITLE')}
              </Button>
            }
          />
        ) : (
          <ModalFooter
            actions={
              <Button
                type="primary"
                size="large"
                onClick={() => setOpen(false)}
              >
                {t('LOG_IN_TEXT')}
              </Button>
            }
          />
        )
      }
    >
      <Flex vertical gap={SPACE_MD}>
        <Typography.Title level={2}>
          {t('RESET_PASSWORD_TITLE')}
        </Typography.Title>
        {!email ? (
          <>
            <Typography.Text type="secondary">
              {t('RESET_PASSWORD_TEXT')}
            </Typography.Text>
            <FormErrorAlert
              error={resetMutation.error}
              fallbackKey="CLIENT_LOGIN_NOT_ALLOWED"
            />
            <SchemaForm
              form={form}
              formSchema={formSchema}
              name="reset-request"
              scrollToFirstError
              onFinish={(values: { email: string }) => void onFinish(values)}
              sections={sections}
            />
          </>
        ) : (
          <>
            <Typography.Title level={2}>
              {t('EMAIL_SENT_TEXT')}
            </Typography.Title>
            <Typography.Text type="secondary">
              {t('RESET_PASSWORD_SUCCESS_TEXT_1')}{' '}
              <Typography.Text>{email}</Typography.Text>,{' '}
              {t('RESET_PASSWORD_SUCCESS_TEXT_2')}
            </Typography.Text>
          </>
        )}
      </Flex>
    </Drawer>
  );
};

export default ResetPasswordPopUp;
